
import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { addDays, subDays } from 'date-fns';
import { recipeService } from '@/services/recipe';
import { RECIPES } from '@/constants/queryKeys';
import { useUser } from '@/contexts/UserContext';
import { useIsNewUser } from '@/hooks/useIsNewUser';
import { useShoppingListCount } from '@/hooks/useShoppingListCount';
import { usePersonalRecipes } from '@/hooks/usePersonalRecipes';
import { usePersonalMealPlans } from '@/hooks/usePersonalMealPlans';
import { usePersonalShoppingLists } from '@/hooks/usePersonalShoppingLists';
import { useNextScheduledMeal } from '@/hooks/useNextScheduledMeal';
import { useCurrentUserFamily } from '@/hooks/useCurrentUserFamily';
import { useMealPlans } from '@/hooks/useMealPlans';
import { useRecipes } from '@/hooks/useRecipes';
import { useMealCompositions } from '@/hooks/useMealCompositions';
import { useMealNotes } from '@/hooks/useMealNotes';
import { useProfileData } from '@/hooks/useProfileData';
import OnboardingSteps from './OnboardingSteps';
import OnboardingBenefits from './OnboardingBenefits';

const OnboardingFlow: React.FC = () => {
  const { isAuthenticated, user } = useUser();
  const { totalMealPlansCount } = useIsNewUser();
  const { data: totalShoppingListCount = 0 } = useShoppingListCount();

  // Date range for meal data (same as LandingPage)
  const today = new Date();
  const yesterday = subDays(today, 1);
  const tomorrow = addDays(today, 1);
  
  // Get family info
  const { familyId } = useCurrentUserFamily();
  
  // Get family invite data for collaboration widget
  const { inviteCode, isFamilyAdmin, isSingleMemberFamily } = useProfileData();

  // Fetch all accessible recipes (family recipes)
  const { data: allRecipes = [] } = useQuery({
    queryKey: [RECIPES],
    queryFn: recipeService.getRecipes,
    enabled: isAuthenticated && !!user,
  });

  // Fetch meal data for preview (same as LandingPage)
  const { data: mealPlans = [], isLoading: mealPlansLoading } = useMealPlans(yesterday, tomorrow, familyId);
  const { data: recipes = [], isLoading: recipesLoading } = useRecipes();
  const { data: mealCompositions = [], isLoading: compositionsLoading } = useMealCompositions();
  const { data: mealNotes = [], isLoading: mealNotesLoading } = useMealNotes(yesterday, tomorrow, familyId);

  // Fetch personal counts for completion and achievement messaging
  const { data: personalRecipes = [] } = usePersonalRecipes();
  const { data: personalMealPlansCount = 0 } = usePersonalMealPlans();
  const { data: personalShoppingListCount = 0 } = usePersonalShoppingLists();
  const { data: nextMeal } = useNextScheduledMeal();

  // Loading state for meal data
  const mealDataLoading = mealPlansLoading || recipesLoading || compositionsLoading || mealNotesLoading;

  const totalAccessibleRecipes = allRecipes.length;
  const personalRecipeCount = personalRecipes.length;

  return (
    <div className="space-y-6">
      <OnboardingSteps 
        personalRecipeCount={personalRecipeCount}
        totalAccessibleRecipes={totalAccessibleRecipes}
        personalMealPlansCount={personalMealPlansCount}
        totalMealPlansCount={totalMealPlansCount}
        personalShoppingListCount={personalShoppingListCount}
        totalShoppingListCount={totalShoppingListCount}
        latestRecipe={personalRecipes[0]} // Pass latest recipe
        nextMeal={nextMeal}
        mealPlans={mealPlans}
        recipes={recipes}
        mealCompositions={mealCompositions}
        mealNotes={mealNotes}
        mealDataLoading={mealDataLoading}
      />
      <OnboardingBenefits 
        inviteCode={inviteCode}
        isFamilyAdmin={isFamilyAdmin}
        isSingleMemberFamily={isSingleMemberFamily}
        userId={user?.id}
      />
    </div>
  );
};

export default OnboardingFlow;
